import { Button, Toaster, Wordmark, cn } from '@westhetics/ui'
import type { ReactNode } from 'react'
import { Link, Outlet, useNavigate } from 'react-router'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'

/**
 * A casca do portal (docs/areas/portal-do-paciente.md, RN-POR-14): mobile first, uma coluna de
 * leitura de no máximo 640px, o wordmark e "Sair" no alto, as áreas seguras do aparelho em volta.
 * Sem sidebar, sem barra inferior: a navegação cabe na lista e no "Voltar".
 */

/** A coluna de leitura: centrada, no máximo 640px. */
export const column = 'mx-auto w-full max-w-reading'

/** Respiro lateral de 20px, nunca menor que a área segura (o entalhe, com o aparelho deitado). */
export const gutter =
  'pr-[max(1.25rem,env(safe-area-inset-right))] pl-[max(1.25rem,env(safe-area-inset-left))]'

/** O parágrafo de apoio das telas do portal. */
export const supportText = 'font-body text-secondary text-[13px] leading-[1.6] font-light'

/** A linha miúda: rodapé, nota de rodapé de bloco. */
export const noteText = 'font-body text-label text-[12px] leading-[1.6] font-light'

export function PortalShell() {
  const session = useSession()
  const navigate = useNavigate()
  return (
    <div className="bg-pampas text-ink flex min-h-dvh flex-col">
      <header className="border-hairline bg-pampas pt-safe sticky top-0 z-10 border-b">
        <div className={cn(column, gutter, 'h-header flex items-center justify-between gap-4')}>
          <Link to={routes.home} aria-label="Portal do paciente — início" className="block">
            <Wordmark className="text-ink h-[13px] w-auto" />
          </Link>
          <Button
            variant="text"
            size="sm"
            onClick={() => {
              void session.signOut().then(() => navigate(routes.signIn, { replace: true }))
            }}
          >
            Sair
          </Button>
        </div>
      </header>
      <main className={cn(column, gutter, 'flex-1 py-6')}>
        <Outlet />
      </main>
      <footer
        className={cn(column, gutter, noteText, 'pb-safe border-hairline mt-4 border-t py-5')}
      >
        Sessão de 30 dias neste aparelho · © 2026 Westhetics · A beleza deve ser segura.
      </footer>
      <Toaster />
    </div>
  )
}

/** "← Voltar ao início", em texto, no alto de uma leitura. */
export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Button asChild variant="text" size="sm">
      <Link to={to}>← {children}</Link>
    </Button>
  )
}

/** O estado "carregando" de uma leitura: o esqueleto com a forma das linhas, nunca spinner. */
export function ReadingSkeleton({ label }: { label: string }) {
  return (
    <div role="status" aria-label={label} className="flex flex-col gap-4">
      <div className="bg-alto h-[11px] w-[160px]" />
      <div className="bg-alto h-[26px] w-[260px] max-w-full" />
      <div className="bg-alto h-[13px] w-[320px] max-w-full" />
      <div className="bg-alto mt-4 h-[180px] w-full" />
    </div>
  )
}
